import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getDateLocale } from '../i18n';
import { patchJiraTicket, formatApiError, insertJiraImputation, getOperations, getUsers } from '../services/api';

/** Jira workflow-style status options (stored as shown). */
export const JIRA_STATUS_OPTIONS = [
  'To do',
  'In progress',
  'Sourced',
  'Proposed',
  'Presented',
  'Final',
  'Completed',
];

function formatOperationOptionLabel(op) {
  if (!op || typeof op !== 'object') return '';
  const lp = (op.production_line_lp || '').trim();
  const act = (op.activity || '').trim();
  if (lp && act) return `${lp} · ${act}`;
  return act || lp || '';
}

function normalizeMatchKey(value) {
  return String(value || '')
    .normalize('NFKC')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

async function fetchAllOperations() {
  const all = [];
  let page = 1;
  const pageSize = 100;
  for (;;) {
    const res = await getOperations({}, page, pageSize);
    const rows = Array.isArray(res?.results) ? res.results : [];
    if (!rows.length) break;
    all.push(...rows);
    if (rows.length < pageSize) break;
    page += 1;
    if (page > 100) break;
  }
  return all;
}

/** Employees with email (tenant users list). */
async function fetchAllStaffUsers() {
  const all = [];
  let page = 1;
  const pageSize = 100;
  for (;;) {
    const res = await getUsers({}, page, pageSize);
    const rows = Array.isArray(res?.results) ? res.results : [];
    if (!rows.length) break;
    const staff = rows.filter((u) => u.role === 'EMPLOYE');
    all.push(...staff);
    if (rows.length < pageSize) break;
    page += 1;
    if (page > 100) break;
  }
  const seen = new Set();
  return all.filter((u) => {
    const em = (u.email || '').trim();
    if (!em || seen.has(em)) return false;
    seen.add(em);
    return true;
  });
}

function userOptionLabel(u) {
  const name = [u.first_name, u.last_name].filter(Boolean).join(' ').trim();
  return name ? `${name} (${u.email})` : u.email;
}

export const formatJiraLongField = (value) => {
  if (value == null || value === '') return '—';
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value, null, 2);
    } catch {
      return String(value);
    }
  }
  if (typeof value === 'string') {
    const t = value.trim();
    if ((t.startsWith('{') && t.endsWith('}')) || (t.startsWith('[') && t.endsWith(']'))) {
      try {
        return JSON.stringify(JSON.parse(t), null, 2);
      } catch {
        return value;
      }
    }
  }
  return String(value);
};

export const formatJiraDateTime = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString(getDateLocale());
};

/** Jira ADF or plain text → readable string */
function commentBodyToText(body) {
  if (body == null || body === '') return '';
  if (typeof body === 'string') return body;
  if (typeof body === 'object') {
    if (typeof body.text === 'string') return body.text;
    if (Array.isArray(body.content)) {
      const walk = (nodes) => {
        if (!Array.isArray(nodes)) return '';
        return nodes
          .map((node) => {
            if (!node || typeof node !== 'object') return '';
            if (node.text) return node.text;
            if (node.content) return walk(node.content);
            return '';
          })
          .join('');
      };
      const t = walk(body.content);
      if (t) return t;
    }
    try {
      return JSON.stringify(body);
    } catch {
      return String(body);
    }
  }
  return String(body);
}

function extractAuthor(raw) {
  if (!raw || typeof raw !== 'object') return '';
  const a = raw.author;
  if (typeof a === 'string') return a;
  if (a && typeof a === 'object') {
    return (
      a.displayName ||
      a.name ||
      a.emailAddress ||
      a.accountId ||
      ''
    );
  }
  return (
    raw.authorName ||
    raw.authorEmail ||
    raw.userName ||
    raw.displayName ||
    raw.user?.displayName ||
    raw.user?.name ||
    ''
  );
}

function normalizeCommentEntry(raw) {
  if (raw == null) return null;
  if (typeof raw === 'string') {
    const t = raw.trim();
    return t
      ? { title: '', author: '', body: t, date: null }
      : null;
  }
  if (typeof raw !== 'object') return null;

  const title = (raw.title || raw.subject || raw.summary || '').trim();
  const author = String(extractAuthor(raw) || raw.author || '').trim();
  const body = commentBodyToText(raw.body ?? raw.text ?? raw.content ?? raw.message ?? '').trim();
  const dateRaw =
    raw.created ?? raw.updated ?? raw.date ?? raw.createdAt ?? raw.timestamp ?? raw.time ?? null;

  return {
    title,
    author,
    body,
    date: dateRaw,
  };
}

/**
 * Prefer structured ticket_comments; else parse commentaire JSON array; else single string.
 */
export function getJiraCommentItems(ticket) {
  const fromStructured = ticket?.ticket_comments;
  if (Array.isArray(fromStructured) && fromStructured.length > 0) {
    return fromStructured.map(normalizeCommentEntry).filter(Boolean);
  }
  if (fromStructured && typeof fromStructured === 'object' && !Array.isArray(fromStructured)) {
    const one = normalizeCommentEntry(fromStructured);
    return one ? [one] : [];
  }

  const raw = ticket?.commentaire;
  if (raw == null || raw === '') return [];
  if (typeof raw === 'string') {
    const t = raw.trim();
    if (!t) return [];
    if ((t.startsWith('[') && t.endsWith(']')) || (t.startsWith('{') && t.endsWith('}'))) {
      try {
        const parsed = JSON.parse(t);
        if (Array.isArray(parsed)) {
          return parsed.map(normalizeCommentEntry).filter(Boolean);
        }
        if (parsed && typeof parsed === 'object') {
          const one = normalizeCommentEntry(parsed);
          return one ? [one] : [];
        }
      } catch {
        return [{ title: '', author: '', body: t, date: null }];
      }
    }
    return [{ title: '', author: '', body: t, date: null }];
  }
  if (Array.isArray(raw)) {
    return raw.map(normalizeCommentEntry).filter(Boolean);
  }
  return [];
}

function commentHeadline({ title, author }, fallback = 'Comment') {
  if (title && author) return `${title} (${author})`;
  if (title) return title;
  if (author) return author;
  return fallback;
}

const fieldClass =
  'w-full py-2 px-3 text-sm border border-border rounded-lg bg-background text-primary placeholder:text-text-secondary focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary';

/**
 * Scrollable ticket detail body for the notification slide panel (RS Jira admin).
 */
const JiraTicketSidebarContent = ({ ticket, onTicketUpdated }) => {
  const { t } = useTranslation(['admin', 'common']);
  const [form, setForm] = useState({
    title: '',
    assigned_user_email: '',
    previous_status: '',
    new_status: '',
    ticket_description: '',
  });
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [inserting, setInserting] = useState(false);
  const [insertError, setInsertError] = useState(null);
  const [insertSuccess, setInsertSuccess] = useState(false);
  const [autoDetectMessage, setAutoDetectMessage] = useState(null);
  const [autoDetectError, setAutoDetectError] = useState(null);
  const [operations, setOperations] = useState([]);
  const [operationsLoading, setOperationsLoading] = useState(false);
  const [staffUsers, setStaffUsers] = useState([]);
  const [staffLoading, setStaffLoading] = useState(false);
  const [catalogError, setCatalogError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setCatalogError(null);
      setOperationsLoading(true);
      setStaffLoading(true);
      try {
        const [ops, users] = await Promise.all([fetchAllOperations(), fetchAllStaffUsers()]);
        if (!cancelled) {
          setOperations(ops);
          setStaffUsers(users);
        }
      } catch (e) {
        if (!cancelled) {
          setCatalogError(t('jira.catalogLoadFailed'));
          console.error(e);
        }
      } finally {
        if (!cancelled) {
          setOperationsLoading(false);
          setStaffLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [t]);

  useEffect(() => {
    if (!ticket) return;
    setForm({
      title: ticket.title ?? '',
      assigned_user_email: ticket.assigned_user_email ?? '',
      previous_status: ticket.previous_status ?? '',
      new_status: ticket.new_status ?? '',
      ticket_description: ticket.ticket_description ?? '',
    });
    setSaveError(null);
    setSaveSuccess(false);
    setInsertError(null);
    setInsertSuccess(false);
    setAutoDetectMessage(null);
    setAutoDetectError(null);
  }, [ticket]);

  const isDirty = useMemo(() => {
    if (!ticket) return false;
    return (
      (form.title ?? '') !== (ticket.title ?? '') ||
      (form.assigned_user_email ?? '') !== (ticket.assigned_user_email ?? '') ||
      (form.previous_status ?? '') !== (ticket.previous_status ?? '') ||
      (form.new_status ?? '') !== (ticket.new_status ?? '') ||
      (form.ticket_description ?? '') !== (ticket.ticket_description ?? '')
    );
  }, [form, ticket]);

  const handleSave = async () => {
    if (!ticket?.id || saving || !isDirty) return;
    setSaving(true);
    setSaveError(null);
    setSaveSuccess(false);
    try {
      const updated = await patchJiraTicket(ticket.id, {
        title: form.title,
        assigned_user_email: form.assigned_user_email,
        previous_status: form.previous_status,
        new_status: form.new_status,
        ticket_description: form.ticket_description,
      });
      onTicketUpdated?.(updated);
      setSaveSuccess(true);
      window.setTimeout(() => setSaveSuccess(false), 2500);
    } catch (err) {
      const formatted = formatApiError(err);
      setSaveError(formatted.generalError || t('jira.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const comments = useMemo(() => {
    const items = getJiraCommentItems(ticket);
    const withTime = items.map((c, i) => ({
      ...c,
      _key: i,
      _sort: (() => {
        if (!c.date) return i;
        const t = new Date(c.date).getTime();
        return Number.isNaN(t) ? i : t;
      })(),
    }));
    withTime.sort((a, b) => a._sort - b._sort);
    return withTime;
  }, [ticket]);

  const operationLabels = useMemo(() => {
    const labels = [];
    const seen = new Set();
    operations.forEach((op) => {
      const label = formatOperationOptionLabel(op);
      if (label && !seen.has(label)) {
        seen.add(label);
        labels.push(label);
      }
    });
    labels.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
    return labels;
  }, [operations]);

  const emailOptions = useMemo(
    () =>
      [...staffUsers].sort((a, b) =>
        (a.email || '').localeCompare(b.email || '', undefined, { sensitivity: 'base' })
      ),
    [staffUsers]
  );

  const insertBlockers = useMemo(() => {
    const msgs = [];
    if (!ticket) return msgs;
    if (ticket.has_imputation) {
      msgs.push(t('jira.alreadyHasImputation'));
      return msgs;
    }
    if (operationsLoading || staffLoading) {
      msgs.push(t('jira.loadingOpsAndEmployees'));
      return msgs;
    }
    const em = (form.assigned_user_email || '').trim().toLowerCase();
    if (em && !staffUsers.some((u) => (u.email || '').toLowerCase() === em)) {
      msgs.push(t('jira.assignedEmailMustBeEmployee'));
    }
    if (!(form.previous_status || '').trim()) {
      msgs.push(t('jira.selectPreviousStatus'));
    }
    if (!(form.new_status || '').trim()) {
      msgs.push(t('jira.selectNewStatus'));
    }
    return msgs;
  }, [ticket, form, staffUsers, operationsLoading, staffLoading, t]);

  const canInsertImputation = insertBlockers.length === 0;
  const insertDisabledReason = useMemo(() => {
    if (inserting) return t('jira.insertInProgress');
    if (ticket?.has_imputation) return t('jira.insertDisabledLinked');
    if (insertBlockers.length > 0) return t('jira.insertDisabledReason', { reasons: insertBlockers.join(' ') });
    return null;
  }, [inserting, ticket, insertBlockers, t]);

  const handleAutoDetectAssignedEmail = () => {
    setAutoDetectMessage(null);
    setAutoDetectError(null);

    if (staffLoading) {
      setAutoDetectError(t('jira.employeesStillLoading'));
      return;
    }

    const candidateName = (
      ticket?.current_user_name ||
      ticket?.assigned_user_name ||
      ticket?.utilisateur ||
      ''
    ).trim();

    if (!candidateName) {
      setAutoDetectError(t('jira.noAssignedUserName'));
      return;
    }

    const normalize = (v) => String(v || '').replace(/\s+/g, ' ').trim().toLowerCase();
    const target = normalize(candidateName);
    const match = staffUsers.find((u) => normalize(`${u.first_name} ${u.last_name}`) === target);

    if (!match?.email) {
      setAutoDetectError(t('jira.noUserFoundByName', { name: candidateName }));
      return;
    }

    setForm((f) => ({ ...f, assigned_user_email: match.email }));
    setAutoDetectMessage(t('jira.detectedEmailFromName', { email: match.email, name: candidateName }));
  };

  const handleInsertImputation = async () => {
    // #region agent log
    fetch('http://127.0.0.1:7365/ingest/0550adc7-17c4-4a8d-9723-9db3de8f3996',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'7e2ff2'},body:JSON.stringify({sessionId:'7e2ff2',runId:'run1',hypothesisId:'H1',location:'JiraTicketSidebarContent.js:handleInsertImputation:start',message:'Insert button pressed',data:{ticketPk:ticket?.id,ticketHasImputation:ticket?.has_imputation,canInsertImputation,insertBlockersCount:insertBlockers.length},timestamp:Date.now()})}).catch(()=>{});
    // #endregion
    if (!ticket?.id || inserting) return;
    if (!canInsertImputation) {
      const reason =
        insertBlockers.length > 0
          ? insertBlockers.join(' ')
          : t('jira.requiredFieldsMissing');
      // #region agent log
      fetch('http://127.0.0.1:7365/ingest/0550adc7-17c4-4a8d-9723-9db3de8f3996',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'7e2ff2'},body:JSON.stringify({sessionId:'7e2ff2',runId:'run1',hypothesisId:'H2',location:'JiraTicketSidebarContent.js:handleInsertImputation:blocked',message:'Insert blocked client-side',data:{reason,insertBlockers,ticketPk:ticket?.id},timestamp:Date.now()})}).catch(()=>{});
      // #endregion
      setInsertSuccess(false);
      setInsertError(t('jira.imputationNotCreated', { reason }));
      return;
    }
    setInserting(true);
    setInsertError(null);
    setInsertSuccess(false);
    try {
      const res = await insertJiraImputation(ticket.id, {
        title: form.title.trim(),
        assigned_user_email: form.assigned_user_email.trim(),
        assigned_user_name: ticket?.assigned_user_name || '',
        current_user_email: ticket?.current_user_email || '',
        current_user_name: ticket?.current_user_name || '',
        previous_status: form.previous_status.trim(),
        new_status: form.new_status.trim(),
        ticket_description: form.ticket_description,
      });
      // #region agent log
      fetch('http://127.0.0.1:7365/ingest/0550adc7-17c4-4a8d-9723-9db3de8f3996',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'7e2ff2'},body:JSON.stringify({sessionId:'7e2ff2',runId:'run1',hypothesisId:'H3',location:'JiraTicketSidebarContent.js:handleInsertImputation:apiSuccess',message:'Insert API resolved',data:{ticketPk:ticket?.id,responseHasJiraTicket:!!res?.jira_ticket,responseJiraTicketHasImputation:res?.jira_ticket?.has_imputation,responseImputationId:res?.imputation_id ?? null},timestamp:Date.now()})}).catch(()=>{});
      // #endregion
      if (res?.jira_ticket) {
        onTicketUpdated?.(res.jira_ticket);
      }
      setInsertSuccess(true);
      window.setTimeout(() => setInsertSuccess(false), 2500);
    } catch (err) {
      const formatted = formatApiError(err);
      // #region agent log
      fetch('http://127.0.0.1:7365/ingest/0550adc7-17c4-4a8d-9723-9db3de8f3996',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'7e2ff2'},body:JSON.stringify({sessionId:'7e2ff2',runId:'run1',hypothesisId:'H4',location:'JiraTicketSidebarContent.js:handleInsertImputation:apiError',message:'Insert API rejected',data:{ticketPk:ticket?.id,errorMessage:formatted.generalError || t('jira.createImputationFailed')},timestamp:Date.now()})}).catch(()=>{});
      // #endregion
      setInsertError(formatted.generalError || t('jira.createImputationFailed'));
    } finally {
      setInserting(false);
    }
  };

  if (!ticket) return null;

  return (
    <div className="px-4 py-3 space-y-4 text-left">
      <div className="pt-1 border-t border-border">
        <p className="text-xs font-medium text-text-secondary uppercase tracking-wide mb-0.5">{t('jira.createdAt')}</p>
        <p className="text-sm text-primary">{formatJiraDateTime(ticket.created_at)}</p>
      </div>
      <div>
        <p className="text-xs font-medium text-text-secondary uppercase tracking-wide mb-1">{t('jira.ticketId')}</p>
        <p className="text-sm text-primary font-semibold break-all">{ticket.ticket_id || '—'}</p>
      </div>
      <div>
        <p className="text-xs font-medium text-text-secondary uppercase tracking-wide mb-0.5">{t('jira.assigneeName')}</p>
        <p className="text-sm text-primary">
          {ticket.assigned_user_name || ticket.utilisateur || '—'}
        </p>
      </div>
      {catalogError && (
        <p className="text-xs text-amber-700 dark:text-amber-400" role="status">
          {catalogError}
        </p>
      )}

      <div>
        <label htmlFor="jira-ticket-title" className="text-xs font-medium text-text-secondary uppercase tracking-wide mb-1 block">
          {t('jira.ticketTitle')}
        </label>
        <p className="text-[11px] text-text-secondary mb-1.5">
          {t('jira.titleHint')}
          {operationsLoading && <span className="ml-1 opacity-70">{t('jira.loadingOperations')}</span>}
        </p>
        <input
          id="jira-ticket-title"
          type="text"
          value={form.title}
          onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
          className={fieldClass}
          maxLength={255}
          autoComplete="off"
          list="jira-operation-options"
          placeholder=""
        />
        <datalist id="jira-operation-options">
          {operationLabels.map((label) => (
            <option key={label} value={label} />
          ))}
        </datalist>
      </div>

      <div>
        <label htmlFor="jira-assigned-email" className="text-xs font-medium text-text-secondary uppercase tracking-wide mb-1 block">
          {t('jira.assignedEmail')}
        </label>
        <p className="text-[11px] text-text-secondary mb-1.5">
          {t('jira.assignedEmailHint')}
          {staffLoading && <span className="ml-1 opacity-70">{t('jira.loadingEmployees')}</span>}
        </p>
        <div className="flex items-center gap-2">
          <input
            id="jira-assigned-email"
            type="email"
            value={form.assigned_user_email}
            onChange={(e) => setForm((f) => ({ ...f, assigned_user_email: e.target.value }))}
            className={fieldClass}
            maxLength={254}
            autoComplete="off"
            list="jira-staff-email-options"
            placeholder=""
          />
          <button
            type="button"
            onClick={handleAutoDetectAssignedEmail}
            disabled={staffLoading}
            className="shrink-0 h-[38px] px-3 text-xs font-semibold rounded-lg border border-primary text-primary bg-background hover:bg-primary/10 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            title={t('jira.autoDetectByNameTitle')}
          >
            {t('jira.autoDetect')}
          </button>
        </div>
        {autoDetectError && (
          <p className="text-[11px] text-red-600 dark:text-red-400 mt-1" role="status">
            {autoDetectError}
          </p>
        )}
        {autoDetectMessage && (
          <p className="text-[11px] text-emerald-700 dark:text-emerald-400 mt-1" role="status">
            {autoDetectMessage}
          </p>
        )}
        <datalist id="jira-staff-email-options">
          {emailOptions.map((u) => (
            <option key={u.id} value={u.email}>
              {userOptionLabel(u)}
            </option>
          ))}
        </datalist>
      </div>

      <div>
        <label htmlFor="jira-prev-status" className="text-xs font-medium text-text-secondary uppercase tracking-wide mb-1 block">
          {t('jira.previousStatus')}
        </label>
        <select
          id="jira-prev-status"
          value={form.previous_status}
          onChange={(e) => setForm((f) => ({ ...f, previous_status: e.target.value }))}
          className={fieldClass}
        >
          <option value="">—</option>
          {!JIRA_STATUS_OPTIONS.includes(form.previous_status) && form.previous_status ? (
            <option value={form.previous_status}>{form.previous_status} {t('jira.currentStatusSuffix')}</option>
          ) : null}
          {JIRA_STATUS_OPTIONS.map((s) => (
            <option key={`prev-${s}`} value={s}>
              {s}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label htmlFor="jira-new-status" className="text-xs font-medium text-text-secondary uppercase tracking-wide mb-1 block">
          {t('jira.newStatus')}
        </label>
        <select
          id="jira-new-status"
          value={form.new_status}
          onChange={(e) => setForm((f) => ({ ...f, new_status: e.target.value }))}
          className={fieldClass}
        >
          <option value="">—</option>
          {!JIRA_STATUS_OPTIONS.includes(form.new_status) && form.new_status ? (
            <option value={form.new_status}>{form.new_status} {t('jira.currentStatusSuffix')}</option>
          ) : null}
          {JIRA_STATUS_OPTIONS.map((s) => (
            <option key={`new-${s}`} value={s}>
              {s}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label htmlFor="jira-description" className="text-xs font-medium text-text-secondary uppercase tracking-wide mb-1 block">
          {t('jira.description')}
        </label>
        <textarea
          id="jira-description"
          value={form.ticket_description}
          onChange={(e) => setForm((f) => ({ ...f, ticket_description: e.target.value }))}
          className={`${fieldClass} min-h-[180px] max-h-[min(50vh,320px)] resize-y font-sans`}
          rows={8}
          spellCheck="true"
          placeholder=""
        />
      </div>

      <div className="flex flex-col gap-2 pt-1">
        {saveError && (
          <p className="text-xs text-red-600 dark:text-red-400" role="alert">
            {saveError}
          </p>
        )}
        {insertError && (
          <p className="text-xs text-red-600 dark:text-red-400" role="alert">
            {insertError}
          </p>
        )}
        {saveSuccess && (
          <p className="text-xs text-emerald-700 dark:text-emerald-400">{t('jira.saved')}</p>
        )}
        {insertSuccess && (
          <p className="text-xs text-emerald-700 dark:text-emerald-400">{t('jira.imputationCreated')}</p>
        )}
        <div className="flex gap-2">
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || !isDirty}
            className="flex-1 min-w-0 py-2.5 px-3 text-sm font-semibold rounded-lg bg-primary text-white hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity"
          >
            {saving ? t('common:saving') : t('common:save')}
          </button>
          <button
            type="button"
            onClick={handleInsertImputation}
            disabled={inserting}
            className="flex-1 min-w-0 py-2.5 px-3 text-sm font-semibold rounded-lg border border-primary text-primary bg-background hover:bg-primary/10 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {inserting ? t('jira.insertingOperation') : t('jira.insertOperation')}
          </button>
        </div>
        {insertDisabledReason && (
          <p className="text-[11px] text-text-secondary leading-snug" role="status">
            {insertDisabledReason}
          </p>
        )}
      </div>

      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <span className="material-symbols-outlined text-[20px] text-primary/80 shrink-0" aria-hidden>
            chat_bubble
          </span>
          <span className="text-xs font-semibold text-primary tracking-wide">{t('jira.commentaire')}</span>
        </div>

        {comments.length === 0 ? (
          <p className="text-sm text-text-secondary/70 italic leading-relaxed pl-1">
            {t('jira.noCommentsOnTicket')}
          </p>
        ) : (
          <ul className="space-y-3 list-none m-0 p-0">
            {comments.map((c) => (
              <li
                key={c._key}
                className="rounded-xl border border-slate-200/90 dark:border-slate-600/80 bg-gradient-to-br from-slate-50 to-slate-100/90 dark:from-slate-800/90 dark:to-slate-800/60 shadow-sm ring-1 ring-black/5 dark:ring-white/10 overflow-hidden"
              >
                <div className="px-3.5 pt-3 pb-2 border-b border-slate-200/70 dark:border-slate-600/50 bg-white/40 dark:bg-slate-900/20">
                  <p className="text-sm font-semibold text-primary leading-snug">
                    {commentHeadline(c, t('jira.comment'))}
                  </p>
                  {c.date && (
                    <p className="text-[11px] text-text-secondary mt-1 tabular-nums">
                      {formatJiraDateTime(c.date)}
                    </p>
                  )}
                </div>
                <div className="px-3.5 py-3 max-h-48 overflow-y-auto">
                  {c.body ? (
                    <p className="text-sm text-primary leading-relaxed whitespace-pre-wrap break-words font-normal">
                      {c.body}
                    </p>
                  ) : (
                    <p className="text-sm text-text-secondary/60 italic">{t('jira.noText')}</p>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default JiraTicketSidebarContent;
